/**
 * 管理单个选择上下文的最新 Hover 坐标与失效状态。
 *
 * 不创建 RAF、不保留 MouseEvent、Intersection 或 DOM 引用。Viewer 在相机更新后、视觉
 * 结算前调用 flush；按下/点击可以显式结算当前坐标，拖动期间暂停而不丢弃业务事件。
 */
export class EntityHoverInputScheduler {
  /** 当前坐标所属的领域消费者；取消时解除引用。 */
  private consumer: EntityHoverInputConsumer | null = null;
  /** 最新客户端 X 坐标。 */
  private clientX = 0;
  /** 最新客户端 Y 坐标。 */
  private clientY = 0;
  /** 是否存在尚未查询或已经失效的输入。 */
  private pending = false;
  /** 按键操作期间不执行普通 Hover。 */
  private suspended = false;
  /** 防止选择事件订阅重入同一拾取事务。 */
  private consuming = false;
  /** 释放后拒绝新输入。 */
  private disposed = false;
  /** 相机世界矩阵与投影矩阵的数值快照，不依赖可变矩阵对象引用。 */
  private readonly cameraElements = new Float64Array(32);
  /** 是否已经捕获当前上下文的相机状态。 */
  private hasCameraSnapshot = false;
  /** 原始普通 Hover 坐标入队累计数。 */
  private enqueuedInputs = 0;
  /** 静止指针失效累计数。 */
  private invalidations = 0;
  /** 离散事件边界结算累计数。 */
  private boundaryPreparations = 0;
  /** 实际执行 Hover 查询累计数。 */
  private consumedQueries = 0;
  /** 暂停普通 Hover 累计数。 */
  private suspensions = 0;
  /** 抬起恢复 Hover 累计数。 */
  private resumptions = 0;
  /** 实际丢弃有效上下文累计数。 */
  private cancellations = 0;

  /** 当前最新输入的消费者，供选择管理器核对模式/测量路由。 */
  public get owner(): EntityHoverInputConsumer | null { return this.consumer; }

  /**
   * 覆盖最新悬停坐标；该操作不请求 GPU 重绘，真正命中改变才会更新视觉。
   * @param consumer 当前选择模式的消费者。
   * @param clientX 最新客户端 X 坐标。
   * @param clientY 最新客户端 Y 坐标。
   */
  public enqueue(consumer: EntityHoverInputConsumer, clientX: number, clientY: number): void {
    if (this.disposed) return;
    this.enqueuedInputs += 1;
    this.writePendingInput(consumer, clientX, clientY);
  }

  /** 模型或可见性改变后重新查询静止指针；没有有效指针时不创建任务。 */
  public invalidate(): void {
    if (this.consumer && !this.suspended && !this.disposed) {
      this.invalidations += 1;
      this.pending = true;
    }
  }

  /**
   * 比较相机数值快照，确保同坐标在相机移动后重新拾取。
   * @param world 相机当前世界矩阵元素。
   * @param projection 相机当前投影矩阵元素。
   */
  public observeCamera(world: ArrayLike<number>, projection: ArrayLike<number>): void {
    if (!this.consumer || this.disposed) return;
    let changed = false;
    for (let index = 0; index < 16; index++) {
      if (this.cameraElements[index] !== world[index]
        || this.cameraElements[index + 16] !== projection[index]) changed = true;
      this.cameraElements[index] = world[index];
      this.cameraElements[index + 16] = projection[index];
    }
    if (this.hasCameraSnapshot && changed) this.invalidate();
    this.hasCameraSnapshot = true;
  }

  /**
   * 消费一次最新输入；回调期间新到的坐标或显式失效保留给下一帧。
   * 查询错误继续上抛，但错误本身不构成新输入，避免静止时逐帧重复失败。
   * @returns 本次是否真正执行了悬停查询。
   */
  public flush(): boolean {
    if (!this.pending || !this.consumer || this.suspended || this.consuming || this.disposed) return false;
    const consumer = this.consumer;
    const clientX = this.clientX;
    const clientY = this.clientY;
    this.pending = false;
    this.consuming = true;
    this.consumedQueries += 1;
    try {
      consumer.consumeHoverInput(clientX, clientY);
      return true;
    } finally {
      // 不复活已经消费的失败输入，也不清除回调期间真实登记的下一帧任务。
      this.consuming = false;
    }
  }

  /**
   * 在离散事件前结算其当前坐标，避免命令复用上一位置的拓扑代理。
   * @param clientX 按下或点击的客户端 X 坐标。
   * @param clientY 按下或点击的客户端 Y 坐标。
   */
  public prepareBoundary(clientX: number, clientY: number): void {
    if (!this.consumer || this.suspended || this.disposed) return;
    this.boundaryPreparations += 1;
    this.clientX = clientX;
    this.clientY = clientY;
    this.pending = true;
    this.flush();
  }

  /** 按下之后暂停 Hover；原始拖拽仍由原始事件流逐条处理。 */
  public suspend(): void {
    if (this.disposed) return;
    this.suspensions += 1;
    this.pending = false;
    this.suspended = true;
  }

  /**
   * 抬起后以最终坐标恢复 Hover；取消/模式切换已丢弃的上下文不会被复活。
   * @param clientX 抬起坐标 X。
   * @param clientY 抬起坐标 Y。
   */
  public resume(clientX: number, clientY: number): void {
    if (!this.consumer || this.disposed) return;
    this.resumptions += 1;
    this.writePendingInput(this.consumer, clientX, clientY);
  }

  /** 离开、失焦、程序化 Hover 或模式/文档切换时丢弃全部输入上下文。 */
  public cancel(): void {
    if (this.consumer || this.pending || this.suspended || this.hasCameraSnapshot) {
      this.cancellations += 1;
    }
    this.consumer = null;
    this.pending = false;
    this.suspended = false;
    this.hasCameraSnapshot = false;
  }

  /** 解除所有者引用，不拥有任何 DOM 监听或帧循环。 */
  public dispose(): void {
    this.cancel();
    this.disposed = true;
  }

  /**
   * 读取累计工作量快照。
   *
   * @returns 新建的只读数值对象；调用方不能重置调度器内部计数。
   */
  public getStats(): EntityHoverInputSchedulerStats {
    return {
      enqueuedInputs: this.enqueuedInputs,
      invalidations: this.invalidations,
      boundaryPreparations: this.boundaryPreparations,
      consumedQueries: this.consumedQueries,
      suspensions: this.suspensions,
      resumptions: this.resumptions,
      cancellations: this.cancellations,
    };
  }

  /**
   * 写入下一次消费使用的最新输入，但不改变输入来源统计。
   *
   * @param consumer 当前选择模式消费者。
   * @param clientX 客户端 X 坐标。
   * @param clientY 客户端 Y 坐标。
   */
  private writePendingInput(
    consumer: EntityHoverInputConsumer,
    clientX: number,
    clientY: number,
  ): void {
    this.consumer = consumer;
    this.clientX = clientX;
    this.clientY = clientY;
    this.suspended = false;
    this.pending = true;
  }
}
